import { wxApi } from './api';
import store from '@/store/index';
import querystring from 'querystring';
import { Route } from 'vue-router';
import Thumbnail from '@forzoom/thumbnail';
import router from '@/router';
import { ShareOption } from '@/types/wx';
import {
  format as $mathFormat,
  chain as $mathChain,
  bignumber as $mathBignumber,
  im,
} from 'mathjs';
import { createDecorator } from 'vue-class-component';

const ch = [null, '一', '二', '三', '四', '五', '六', '七', '八', '九'];

const chWeekDay = ['日', '一', '二', '三', '四', '五', '六', '日'];

const monthDay = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const shareList = [
  'menuItem:share:appMessage',
  'menuItem:share:timeline',
  'menuItem:share:qq',
  'menuItem:share:weiboApp',
  'menuItem:favorite',
  'menuItem:share:facebook',
  'menuItem:share:QZone',
];

/**
 * 是否是闰年
 */
export function isLeapYear(year: number) {
  if (year % 4 !== 0) {
    return false;
  }
  if (year % 400 === 0) {
    return true;
  }
  if (year % 100 === 0) {
    return false;
  }
  return true;
}

/**
 * 获得一个月有多少天
 *
 * @param {} year
 * @param {} month 1-12
 */
export function getMonthDay(year: number, month: number) {
  if (isLeapYear(year) && month === 2) {
    return 29;
  }
  return monthDay[month];
}

/**
 * 等待毫秒数
 */
export function wait(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
}

/**
 * 获得querystring
 *
 * @return {object} 返回qs
 */
export function getQS(url?: string) {
  const query = url || location.search;
  const posInSearch = query.indexOf('?');
  return querystring.parse(query.substr(posInSearch + 1));
}
/**
 * 是否是null或者undefined
 *
 * @param {} v 参数
 *
 * @return {boolean}
 */
export function isUndef(v: any): v is null | undefined {
  return v === null || v === undefined;
}

/**
 * 金额格式化
 * @param {*} v 金额数据
 * @param {string} division 除数
 * @param {string} precision 精度
 */
export function currency(
  v: number | null | undefined,
  division = 100,
  precision = 2,
): string | null | undefined {
  if (isUndef(v)) {
    return v;
  }

  const result = String(((v as number) / division).toFixed(precision));
  const pos = result.indexOf('.00');
  if (pos !== -1) {
    return result.substr(0, pos);
  } else {
    return result;
  }
}

/**
 * 返回weekDay的中文
 *
 * @param {Date} date
 */
export function toChWeekDay(date: Date, format: string) {
  if (isUndef(date)) {
    return date;
  }
  const day = new Date(date.valueOf() - 8 * 3600000).getUTCDay();
  if (isUndef(chWeekDay[day])) {
    return day;
  }
  if (!isUndef(format)) {
    return format.replace(/d/g, chWeekDay[day]);
  } else {
    return chWeekDay[day];
  }
}

/**
 * 获得当前的时间
 *
 * @return {Date} 当天
 */
export function dateNow() {
  const utcDate = new Date(new Date().valueOf() + 8 * 3600000);
  const year = utcDate.getUTCFullYear();
  const month = utcDate.getUTCMonth() + 1;
  const date = utcDate.getUTCDate();
  const hour = utcDate.getUTCHours();
  const minute = utcDate.getUTCMinutes();
  const second = utcDate.getUTCSeconds();
  return dateParse(`${year}-${month}-${date} ${hour}:${minute}:${second}`);
}

/**
 * 日期格式化
 *
 * @param {Date|Number} v UTC+08:00
 * @param {String} format 格式内容
 *
 * @return {string} 经过format的日期数据
 */
export function dateFormat(v: number | Date, format?: string | null): any {
    const oldV = v
  const type = typeof v;
  if (isUndef(v) || (type !== 'number' && !(v instanceof Date))) {
    return v;
  }
  // 使用默认的format
  if (!format) {
    format = 'Y-m-d';
  }
  if (typeof v === 'number') {
    v = new Date(v - 8 * 3600000);
  } else {
    v = new Date(v.valueOf() - 8 * 3600000);
  }
  let vc = null
  if(format.indexOf('D') > -1 || format.indexOf('M') > -1) {
    if (typeof oldV === 'number') {
        vc = new Date(oldV);
    } else {
        vc = new Date(oldV.valueOf());
    }
  }
  format = format!.replace('Y', String(v.getUTCFullYear()));
  format = format!.replace('m', String(_format(v.getUTCMonth() + 1)));
  format = format!.replace('d', String(_format(v.getUTCDate())));
  if(vc) {
      format = format!.replace('M', String(_format(vc.getMonth() + 1)));
      format = format!.replace('D', String(_format(vc.getDate())));
  }
  format = format!.replace('H', String(_format(v.getUTCHours())));
  format = format!.replace('i', String(_format(v.getUTCMinutes())));
  format = format!.replace('s', String(_format(v.getUTCSeconds())));

  return format;
}

/**
 * 移动日期
 *
 * @param {Date} v 传入进来的日期
 * @param {} day
 */
export function dateOffset(v: Date, day: number) {
  if (isUndef(v)) {
    return v;
  }
  return new Date(v.valueOf() + day * 86400000);
}

/**
 * 日期天数差距
 *
 * @param {} v 较小的日期
 * @param {} c 比较的日期
 *
 * @return {number} 差距天数，当差距小于1天的时候，将不进行处理
 */
export function dateDiff(v: Date, c: Date) {
  if (isUndef(v)) {
    return v;
  }
  return Math.floor((c.valueOf() - v.valueOf()) / 86400000);
}

/**
 * 日期文本解析
 *
 * @param {String} v 要求必须是 1. Y-m-d H:i:s 2. Y-m-d
 *
 * @return {Date} 解析后的日期 UTC+08:00
 */
export function dateParse(v: string): Date; // 重载
export function dateParse(v: '' | null | undefined): null | undefined;
export function dateParse(v: any): any {
  if (isUndef(v) || v === '') {
    return v;
  }
  const chunk = v.split(' ');
  const date = chunk[0];
  const time = chunk[1];
  const dateChunk = date.split('-');
  const d = new Date();
  d.setUTCFullYear(Number(dateChunk[0]));
  d.setUTCMonth(Number(dateChunk[1]) - 1);
  d.setUTCMonth(Number(dateChunk[1]) - 1);
  d.setUTCDate(Number(dateChunk[2]));
  let hour = 0;
  let minute = 0;
  let second = 0;
  if (!isUndef(time)) {
    const timeChunk = time.split(':');
    hour = Number(timeChunk[0]);
    minute = Number(timeChunk[1]);
    second = Number(timeChunk[2]);
  }
  d.setUTCHours(hour);
  d.setUTCMinutes(minute);
  d.setUTCSeconds(second);
  d.setUTCMilliseconds(0);
  return new Date(d.valueOf() + 8 * 3600000);
}

/**
 * 获得当天的Date，要求北京时区
 *
 * @return {Date} 当天日期，UTC时间+8小时
 */
export function dateToday() {
  const utcDate = new Date(new Date().valueOf() + 8 * 3600000);
  return dateParse(
    `${utcDate.getUTCFullYear()}-${utcDate.getUTCMonth() +
      1}-${utcDate.getUTCDate()} 00:00:00`,
  );
}

/**
 * 某一天之后的第一个day
 *
 *  - 当是同一个day的时候会获取当天(默认行为)，当inclusive = false是，将会寻找下一周的同一day
 *
 * @param {} date 某一天的日期
 * @param {} day 周几
 * @param {} inclusive = true 是否包含当天
 */
export function dateNext(date: Date, day: number, inclusive = true) {
  let offset = (day - date.getDay() + 7) % 7;
  if (!inclusive && offset === 0) {
    offset = 7;
  }
  // 得到今天后的周几那天的日期
  return dateOffset(date, offset);
}

export function getYear(date: Date) {
  return date.getUTCFullYear();
}
export function getMonth(date: Date) {
  return date.getUTCMonth();
}
export function getDay(date: Date) {
  return date.getUTCDay();
}
/**
 * 转换成[x天]xx:xx:xx格式
 * @param minute 允许小数
 */
export function minuteFormat(minute: number) {
  const day = Math.floor(minute / 1440);
  const dayStr = day > 0 ? day + '天' : '';
  return (
    dayStr +
    `${_format(Math.floor(minute / 60) % 24)}:${_format(
      Math.floor(minute % 60),
    )}:${_format(Math.round((minute % 1) * 60))}`
  );
}
export function paceFormat(pace: number) {
  return `${Math.floor(pace)}'${_format(Math.floor((pace % 1) * 60))}"`;
}
/**
 * 获取配速
 * @param distance 距离，公里
 * @param time 时长，分钟
 */
export function getPace(distance: number, time: number) {
  return time / distance;
}

export function floorOrFixed(val: number, precision: number = 2) {
  const str = val + '';
  return str.indexOf('.') >= 0 ? val.toFixed(2) : str;
}

/**
 * 对于数字进行format操作
 */
export function _format(n: number) {
  if (n >= 0 && n < 10) {
    return '0' + n;
  } else {
    return `${n}`;
  }
}

// 设置分享话术
export function registerShare(
  option: ShareOption,
  disableShare: boolean = false,
) {
    console.log('target 分享option', option);
  //   if ((window as any).wxstatus != 'ready') {
  //     setTimeout(() => {
  //       registerShare(option, disableShare);
  //     }, 1000);
  //     return;
  //   }
  if (!option.title) {
    option.title = '';
  }
  if (!option.desc) {
    option.desc = '百日跑，看到坚持的力量！';
  }
  if (!option.link) {
    option.link = {
      path: router.currentRoute.fullPath,
    };
  }
  if (option.link && typeof option.link !== 'string') {
    const basicInfo = store.state.user.basicInfo;
    const referrer = basicInfo ? { referrer: basicInfo.id } : {};
    const qs = {
      // 将query对象进行解构
      ...getQS(),
      ...referrer,
      ...(option.link.query ? option.link.query : {}),
    };
    let path: string | null = null;
    if (isString(option.link.path)) {
      path = option.link.path;
    } else if (isPlainObject(option.link.path)) {
      path = router.resolve(option.link.path).resolved.fullPath;
    }
    option.link = `${window.pageUrl}${
      window.appPageRoot
    }?${querystring.stringify(qs)}#${path}`;
  }
  if (!option.imgUrl) {
    const basicInfo =
      store.state.activity.basicInfo[store.getters['activity/activityId']];
    if (basicInfo) {
      option.imgUrl = basicInfo.share_logo;
    }
  }
  if (typeof wx !== 'undefined') {
    store.commit('addJSSDKReadyCallback', async () => {
      if (
        wx.updateAppMessageShareData &&
        isFunction(wx.updateAppMessageShareData)
      ) {
        // 这个是分享接口
        // @ts-ignore
        wx.updateAppMessageShareData(option);
      }
      if (
        wx.updateTimelineShareData &&
        isFunction(wx.updateTimelineShareData)
      ) {
        // @ts-ignore
        wx.updateTimelineShareData(option);
      }

      await Promise.all([
        // @ts-ignore
        wxApi.onMenuShareTimeline({
          ...option,
        }), // 分享到朋友圈
        // @ts-ignore
        wxApi.onMenuShareAppMessage({
          ...option,
        }), // 分享给朋友
        // @ts-ignore
        wxApi.onMenuShareQQ({
          ...option,
        }), // 分享给qq
        // @ts-ignore
        wxApi.onMenuShareWeibo({
          ...option,
        }), // 分享给腾讯微博
        // @ts-ignore
        wxApi.onMenuShareQZone({
          ...option,
        }), // 分享到QQ空间
      ]).catch((err) => {});
    });
  }
}

/**
 * 去除字符串中的空格
 * @param  String str 需要去除空格的字符串
 */
export function removeAllSpace(str: string | null) {
  return str ? str.replace(/\s+/g, '') : '';
}

/**
 * 加载图片
 */
export function loadImage(src: string, anonymous?: boolean) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    if (anonymous) {
      image.setAttribute('crossOrigin', 'anonymous');
    }
    image.onload = () => {
      resolve(image);
    };
    image.onerror = (e) => {
      reject(e);
    };
    image.src = src;
  });
}

/**
 * 加载资源
 */
export function loadScript(path: string) {
  if (typeof wx !== 'undefined') {
    return;
  }
  return new Promise<void>((resolve, reject) => {
    // 尝试重新加载wx内容
    const $script = document.createElement('script');
    $script.setAttribute(
      'src',
      'https://res.wx.qq.com/open/js/jweixin-1.6.0.js',
    );
    $script.onload = () => {
      resolve();
    };
    document.body.appendChild($script);
  });
}

export const is = {
  weixin() {
    return /MicroMessenger/.test(navigator.userAgent);
  },
  tourye() {
    return /Tourye/.test(navigator.userAgent);
  },
  bairipao() {
    return /Bairipao/.test(navigator.userAgent);
  },
  /** 是否是iPhone设备 */
  iPhone() {
    return /iPhone/.test(navigator.userAgent);
  },
  /** 设备是否是iPhoneX/XS/XSMax/XR */
  iPhoneXSeries() {
    return (
      is.iPhoneXOrXS() || is.iPhoneXSMax() || is.iPhoneXSMax() || is.iPhoneXR()
    );
  },
  /** iPhoneXSMax */
  iPhoneXOrXS() {
    return (
      is.iPhone() &&
      window.devicePixelRatio === 3 &&
      window.screen.width === 375 &&
      window.screen.height === 812
    );
  },
  /** iPhoneXSMax */
  iPhoneXSMax() {
    return (
      is.iPhone() &&
      window.devicePixelRatio === 3 &&
      window.screen.width === 414 &&
      window.screen.height === 896
    );
  },
  /** iPhoneR */
  iPhoneXR() {
    return (
      is.iPhone() &&
      window.devicePixelRatio === 2 &&
      window.screen.width === 414 &&
      window.screen.height === 896
    );
  },
};

/**
 * 生成map
 * @param {} arr
 * @param {} key
 */
export function makeMap(arr: any[], key: string, setter?: (v: any) => any) {
  const map: {
    [propName: string]: any;
  } = {};
  for (let i = 0, len = arr.length; i < len; i++) {
    map[arr[i][key]] = setter ? setter(arr[i]) : arr[i];
  }
  return map;
}

export const px2 = {
  rem6(px: number) {
    return px / 37.5;
  },
  toPx(rem: number) {
    return rem * 37.5;
  },
};

/**
 * 获得当前的时间
 *
 * @return {Date} 当天
 */
export function serverDateNow() {
  if (!store.state.util.timestamp) {
    return dateNow();
  }
  const timestamp: number = store.state.util.timestamp * 1000;
  const utcDate = new Date(timestamp + 8 * 3600000);
  const year = utcDate.getUTCFullYear();
  const month = utcDate.getUTCMonth() + 1;
  const time = `${utcDate.getUTCDate()} ${utcDate.getUTCHours()}:${utcDate.getUTCMinutes()}:${utcDate.getUTCSeconds()}`;
  return dateParse(`${year}-${month}-${time}`);
}

export function camelCase(key: string) {
  return key.replace(/[-_][a-z0-9]/g, (c) => c.substr(1).toUpperCase());
}

/**
 * 处理placeholder
 *
 * @param {} val
 * @param {} text
 */
export function placeholder(val: any, text: any, extra: any) {
  if (isUndef(val) || val === extra) {
    return text;
  }
  return val;
}

// 禁用分享
export function disableShare() {
  if (typeof wx !== 'undefined') {
    store.commit('addJSSDKReadyCallback', () => {
      // @ts-ignore
      wxApi.hideMenuItems({
        menuList: shareList,
      });
    });
  }
}

// 启用分享
export function enableShare() {
  if (typeof wx !== 'undefined') {
    store.commit('addJSSDKReadyCallback', () => {
      // @ts-ignore
      wxApi.showMenuItems({
        menuList: shareList,
      });
    });
  }
}

export function supportWebp() {
  if (typeof window === 'undefined') {
    return false;
  }

  let support = true;
  const d = document;

  try {
    const el = d.createElement('object');
    el.type = 'image/webp';
    el.style.visibility = 'hidden';
    el.innerHTML = '!';
    d.body.appendChild(el);
    support = !el.offsetWidth;
    d.body.removeChild(el);
  } catch (err) {
    support = false;
  }

  return support;
}

export const isSupportWebp: boolean = supportWebp();

export let platform: string = '';
export function getPlatform(): string {
  if (!platform) {
    platform = is.weixin() ? 'weixin' : 'web';
  }
  return platform;
}

/** 获取定位 */
export function getWxLocation() {
  if (!is.weixin()) {
    // 返回void则表明不处理
    return Promise.resolve();
  }
  return new Promise<{
    status: 'success' | 'error';
    result?: string;
    message?: string;
    [key: string]: any;
  }>((resolve, reject) => {
    store.commit('addJSSDKReadyCallback', () => {
      wx.getLocation({
        type: 'gcj02', // 返回可以用于wx.openLocation的经纬度
        // @ts-ignore
        success: (res: {
          errMsg: string;
          latitude: number;
          longitude: number;
          speed: any;
          accuracy: any;
        }) => {
          if (res.errMsg === 'getLocation:ok') {
            resolve({
              status: 'success',
              res: {
                latitude: res.latitude,
                longitude: res.longitude,
                speed: res.speed,
                accuracy: res.accuracy,
              },
            });
          } else {
            resolve({
              status: 'error',
              result: res.errMsg,
              message: res.errMsg,
            });
          }
        },
        // @ts-ignore
        fail: (res: WeixinJSSDKCallbackParams) => {
          resolve({
            status: 'error',
            result: res.errMsg,
            message: res.errMsg,
          });
        },
      });
    });
  });
}

/**
 * 是否是某种类型
 */
export function isType<T>(name: string) {
  return (val: any): val is T => {
    return Object.prototype.toString.call(val) === `[object ${name}]`;
  };
}

export const isArray: <T = any>(val: any) => val is T[] =
  Array.isArray || isType('Array');
export const isString: (val: any) => val is string = isType<string>('String');
export const isNumber: (val: any) => val is number = isType<number>('Number');
export const isFunction = isType('Function');
export const isPlainObject = isType('Object');

/**
 * 替换字符串中的某个字段
 * @param {String|Array} str      包含待替换字段的字符串
 * @param {Object} nickname 当前用户的nickname
 */
export function replace(str: string, data: { [key: string]: string }): string;
export function replace(
  str: string[],
  data: { [key: string]: string },
): string[];
export function replace(
  str: string | string[],
  data: { [key: string]: string },
): string | string[] {
  if (!str) {
    return str;
  }
  const result: string[] = [];
  if (!isArray(str)) {
    result.push(str);
  }
  const pending = [];
  for (let i = 0; i < result.length; i++) {
    if (!result[i]) {
      continue;
    }
    const pos = result[i].indexOf('${');
    if (pos >= 0) {
      pending.push(i);
    }
  }

  const keys = Object.keys(data);
  let regex = null;
  for (const key of keys) {
    regex = new RegExp('\\${' + key + '}', 'g');

    for (const index of pending) {
      result[index] = result[index].replace(regex, data[key]);
    }
  }
  return isArray<string>(str) ? result : result[0];
}

export function repeat(str: string, times: number) {
  let result = '';
  for (let i = 0; i < times; i++) {
    result += '*';
  }
  return result;
}

export function securityText(str: string, rule: string) {
  if (!str) {
    return;
  }

  const parts = rule.split('|');
  for (let i = 0, len = parts.length; i < len; i++) {
    const part = parts[i];
    const [key, value] = part.split(':');
    const reverse = key[0] === '!';
    const pureKey = key[0] === '!' ? key.substr(1) : key;
    const strlen = str.length;

    switch (pureKey) {
      // 从head开始计算
      case 'head':
        const hpos = Number(value);
        str = !reverse
          ? repeat('*', hpos) + str.substring(hpos)
          : str.substring(0, hpos) + repeat('*', str.length - hpos);
        break;
      // 从tail开始计算
      case 'tail':
        const tpos = Number(value);
        str = !reverse
          ? str.substring(0, strlen - tpos) + repeat('*', tpos)
          : repeat('*', strlen - tpos) + str.substring(strlen - tpos);
        break;
    }
  }
  return str;
}

export function isPopupDisplay(route: Route, tag?: string) {
  if (!route.meta) {
    return;
  }
  const popup = route.meta.popup;
  return (
    popup === true || (popup && (popup === tag || popup.indexOf(tag) >= 0))
  );
}

/** 文字过长处理
 *
 * @param {string} str 需要处理的文字
 * @param {number} limit 文字长度限制
 */
export function overlongTextProcessing(str: string, limit: number) {
  if (str.length < limit) {
    return str;
  }
  return `${str.slice(0, limit)}...`;
}

const bodyEl = document.body;
let enabled = false;
let top = 0;
export function enableBodyScroll() {
  if (!enabled) {
    bodyEl.style.position = '';
    bodyEl.style.top = '';
    window.scrollTo(0, top); // 回到原先的top
    enabled = true;
  }
}

export function disableBodyScroll() {
  top = window.pageYOffset;
  bodyEl.style.position = 'fixed';
  bodyEl.style.top = -top + 'px';
  enabled = false;
}

/**
 * 显示图片
 * @param current 当前显示图片
 * @param urls 全部图片
 */
export function showImagePopup(current: string, urls: string[]) {
  if (is.weixin()) {
    wx.previewImage({
      current,
      urls,
    });
  }
}

export function gMTToStr(time: Date) {
  const date = new Date(time);
  const str =
    date.getFullYear() +
    '-' +
    (date.getMonth() + 1) +
    '-' +
    date.getDate() +
    ' ' +
    date.getHours() +
    ':' +
    date.getMinutes() +
    ':' +
    date.getSeconds();
  return str;
}

export function gMTToStrDay(time: Date) {
  const date = new Date(time);
  const str =
    date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
  return str;
}

export function traverse(obj: any, keyOrPath: string | string[]) {
  if (!isArray(keyOrPath)) {
    keyOrPath = keyOrPath.split('.');
  }
  if (keyOrPath.length === 1) {
    return obj[keyOrPath[0]];
  } else {
    const last = keyOrPath.pop()!;
    for (const part of keyOrPath) {
      obj = obj[part];
      // 寻找失败
      if (!obj || typeof obj !== 'object') {
        return;
      }
    }
    return obj[last];
  }
}

export const imageFilter = Thumbnail.generateImageFilter({
  bed: {
    aliyun: 'https://static.run100.runorout.cn',
    testAliyun: 'http://static.ro-test.xorout.com',
    default: 'https://static.run100.runorout.cn',
  },
  isAliyun(listener, options) {
    return (
      /static.run100.runorout.cn/.test(listener.src) ||
      /static.ro-test.xorout.com/.test(listener.src)
    );
  },
});

export const EMPTY_LIST = {
  list: [],
  loaded: false,
  requesting: false,
};

/**
 * 生成key
 */
export function getMapKey(obj: object) {
  const keys = Object.keys(obj);
  // @ts-ignore
  return keys
    .sort()
    // @ts-ignore
    .map((key) => (isUndef(obj[key]) ? '' : obj[key]))
    .join('_');
}

export function setTitle(title: string) {
  document.title = title;
  const mobile = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(mobile) && /MicroMessager/.test(mobile)) {
    const iframe = document.createElement('iframe');
    iframe.style.visibility = 'hidden';
    // 替换成站标favicon路径或者任意存在的较小的图片即可
    iframe.setAttribute('src', `${window.pageUrl}/favicon.ico`);
    const iframeCallback = () => {
      setTimeout(() => {
        iframe.removeEventListener('load', iframeCallback);
        document.body.removeChild(iframe);
      }, 0);
    };
    iframe.addEventListener('load', iframeCallback);
    document.body.appendChild(iframe);
  }
}

/**
 * 精度计算，加减乘除
 */
export const math = {
  add(...args: number[]) {
    return comp('add', args);
  },
  subtract(...args: number[]) {
    return comp('subtract', args);
  },
  /** X */
  multiply(...args: number[]) {
    return comp('multiply', args);
  },
  /** % */
  divide(...args: number[]) {
    return comp('divide', args);
  },
};
function comp(_func: string, args: number[]) {
  let t = $mathChain($mathBignumber(args[0]));
  for (let i = 1; i < args.length; i++) {
    // @ts-ignore
    t = t[_func]($mathBignumber(args[i]));
  }
  // 防止超过6位使用科学计数法
  return parseFloat($mathFormat(t.done()));
}
export function Arabia_To_SimplifiedChinese(Num: number | string) {
  if (typeof Num === 'string') {
    for (let i = Num.length - 1; i >= 0; i--) {
      Num = Num.replace(',', ''); // 替换Num中的“,”
      Num = Num.replace(' ', ''); // 替换Num中的空格
    }
  }
  if (isNaN(+Num)) {
    // 验证输入的字符是否为数字
    // alert("请检查小写金额是否正确");
    return;
  }
  // 字符处理完毕后开始转换，采用前后两部分分别转换
  const part = String(Num).split('.');
  let newchar = '';
  // 小数点前进行转化
  for (let i = part[0].length - 1; i >= 0; i--) {
    if (part[0].length > 10) {
      // alert("位数过大，无法计算");
      return '';
    } // 若数量超过拾亿单位，提示
    let tmpnewchar = '';
    const perchar = part[0].charAt(i);
    switch (perchar) {
      case '0':
        tmpnewchar = '零' + tmpnewchar;
        break;
      case '1':
        tmpnewchar = '一' + tmpnewchar;
        break;
      case '2':
        tmpnewchar = '二' + tmpnewchar;
        break;
      case '3':
        tmpnewchar = '三' + tmpnewchar;
        break;
      case '4':
        tmpnewchar = '四' + tmpnewchar;
        break;
      case '5':
        tmpnewchar = '五' + tmpnewchar;
        break;
      case '6':
        tmpnewchar = '六' + tmpnewchar;
        break;
      case '7':
        tmpnewchar = '七' + tmpnewchar;
        break;
      case '8':
        tmpnewchar = '八' + tmpnewchar;
        break;
      case '9':
        tmpnewchar = '九' + tmpnewchar;
        break;
    }
    switch (part[0].length - i - 1) {
      case 0:
        tmpnewchar = tmpnewchar;
        break;
      case 1:
        if (+perchar !== 0) {
          tmpnewchar = tmpnewchar + '十';
        }
        break;
      case 2:
        if (+perchar !== 0) {
          tmpnewchar = tmpnewchar + '百';
        }
        break;
      case 3:
        if (+perchar !== 0) {
          tmpnewchar = tmpnewchar + '千';
        }
        break;
      case 4:
        tmpnewchar = tmpnewchar + '万';
        break;
      case 5:
        if (+perchar !== 0) {
          tmpnewchar = tmpnewchar + '十';
        }
        break;
      case 6:
        if (+perchar !== 0) {
          tmpnewchar = tmpnewchar + '百';
        }
        break;
      case 7:
        if (+perchar !== 0) {
          tmpnewchar = tmpnewchar + '千';
        }
        break;
      case 8:
        tmpnewchar = tmpnewchar + '亿';
        break;
      case 9:
        tmpnewchar = tmpnewchar + '十';
        break;
    }
    newchar = tmpnewchar + newchar;
  }
  // 替换所有无用汉字，直到没有此类无用的数字为止
  while (
    newchar.search('零零') !== -1 ||
    newchar.search('零亿') !== -1 ||
    newchar.search('亿万') !== -1 ||
    newchar.search('零万') !== -1
  ) {
    newchar = newchar.replace('零亿', '亿');
    newchar = newchar.replace('亿万', '亿');
    newchar = newchar.replace('零万', '万');
    newchar = newchar.replace('零零', '零');
  }
  // 替换以“一十”开头的，为“十”
  if (newchar.indexOf('一十') === 0) {
    newchar = newchar.substring(1);
  }
  // 替换以“零”结尾的，为“”
  if (newchar.lastIndexOf('零') === newchar.length - 1 && newchar !== '零') {
    newchar = newchar.substring(0, newchar.length - 1);
  }
  return newchar;
}
const handlerMap: { [key: string]: number | null } = {};
/**
 * 合并调用并延迟，目前只能用于watch
 * @param delay 延迟毫秒
 */
export function WatchDelay(str: string, delay: number) {
  return createDecorator((options, key) => {
    const handlerId = [options.name, str].join('_');
    // @ts-ignore
    const originalMethodName = options.watch[str][0].handler;
    // @ts-ignore
    const originalMethod = options.methods[originalMethodName];

    // @ts-ignore
    options.watch[str] = function wrapperMethod(...args) {
      const handler = handlerMap[handlerId];

      if (handler) {
        window.clearTimeout(handler);
        handlerMap[handlerId] = null;
      }
      handlerMap[handlerId] = window.setTimeout(async () => {
        // @ts-ignore
        originalMethod.apply(this, args);
        handlerMap[handlerId] = null;
      }, delay);
    };
  });
}
